import React, { useState } from 'react';
import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, FolderKanban, Image as ImageIcon, Users, FileText, Menu, X, LogOut, Video, Settings, UserMinus } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';

const NAV_ITEMS = [
  { name: 'Dashboard', path: '/admin', icon: LayoutDashboard },
  { name: 'Projects', path: '/admin/projects', icon: FolderKanban },
  { name: 'Gallery', path: '/admin/gallery', icon: ImageIcon },
  { name: 'Videos', path: '/admin/videos', icon: Video },
  { name: 'Leads', path: '/admin/leads', icon: Users },
  { name: 'Pages', path: '/admin/pages', icon: FileText },
  { name: 'Settings', path: '/admin/settings', icon: Settings },
];

export default function AdminLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

  return (
    <div className="min-h-screen bg-neutral-950 text-white flex overflow-hidden font-sans">
      {/* Mobile Sidebar Overlay */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 md:hidden"
            onClick={toggleSidebar}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <motion.aside
        initial={{ x: -280 }}
        animate={{ x: sidebarOpen ? 0 : -280 }}
        transition={{ type: 'spring', bounce: 0, duration: 0.4 }}
        className="fixed inset-y-0 left-0 z-50 w-72 bg-neutral-900/80 backdrop-blur-xl border-r border-neutral-800/50 flex flex-col md:translate-x-0 md:static"
      >
        <div className="p-6 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="group">
            <h2 className="text-2xl font-light tracking-wider text-white group-hover:text-amber-500 transition-colors">
              HOFLICH <span className="font-bold text-amber-500">ADMIN</span>
            </h2>
          </button>
          <button onClick={toggleSidebar} className="md:hidden text-neutral-400 hover:text-white">
            <X size={24} />
          </button>
        </div>

        <nav className="flex-1 px-4 py-8 space-y-1 overflow-y-auto">
          {NAV_ITEMS.map((item) => {
            const isActive = location.pathname === item.path || (item.path !== '/admin' && location.pathname.startsWith(item.path));
            return (
              <NavLink
                key={item.name}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`relative flex items-center gap-4 px-4 py-3 rounded-xl transition-all duration-300 ${
                  isActive 
                    ? 'bg-amber-500/10 text-amber-500 font-medium' 
                    : 'text-neutral-400 hover:text-white hover:bg-neutral-800/50'
                }`}
              >
                <item.icon size={20} strokeWidth={isActive ? 2.5 : 2} />
                <span>{item.name}</span>
                {isActive && (
                  <motion.div 
                    layoutId="active-pill" 
                    className="absolute left-0 w-1 h-8 bg-amber-500 rounded-r-full" 
                  />
                )}
              </NavLink>
            );
          })}
        </nav>

        <div className="p-4 border-t border-neutral-800/50 space-y-2">
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-4 px-4 py-3 w-full text-neutral-400 hover:text-white transition-colors rounded-xl hover:bg-neutral-800/50"
          >
            <LogOut size={20} />
            <span>Back to Website</span>
          </button>
          <button 
            onClick={() => {
              logout();
              navigate('/admin/login');
            }}
            className="flex items-center gap-4 px-4 py-3 w-full text-red-400 hover:text-red-500 transition-colors rounded-xl hover:bg-red-500/10"
          >
            <UserMinus size={20} />
            <span>Sign Out</span>
          </button>
        </div>
      </motion.aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen relative w-full md:w-[calc(100%-18rem)]">
        {/* Topbar */}
        <header className="sticky top-0 z-30 flex items-center justify-between p-4 md:p-6 bg-neutral-950/80 backdrop-blur-xl border-b border-neutral-800/50">
          <div className="flex items-center gap-4">
            <button onClick={toggleSidebar} className="md:hidden text-neutral-400 hover:text-white">
              <Menu size={24} />
            </button>
            <h1 className="text-xl md:text-2xl font-semibold capitalize">
              {location.pathname === '/admin' ? 'Dashboard' : location.pathname.split('/').pop()}
            </h1>
          </div>
          <div className="flex items-center gap-4">
             <div className="w-10 h-10 rounded-full bg-neutral-800 border border-neutral-700 flex items-center justify-center overflow-hidden">
                <span className="text-sm font-bold text-amber-500">HA</span>
             </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-4 md:p-8 overflow-x-hidden">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
